import { useEffect, useState } from 'react'
import { type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { z } from 'zod'
import { optionalPhone, isMaskSafePhone } from '@/lib/phone'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Button,
	Checkbox,
	ConfirmDialog,
	DateField,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input,
	PhoneInput,
	SectionHeading,
	SelectField,
	Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
	TextField,
} from '@rentflowdev/ui'
import { useUpsertContactKycMutation } from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { useCloseGuard } from '@/lib/use-close-guard'
import { extractErrorMessage } from '@/lib/error-message'
import type { ContactKyc, TenantType } from '@/types'

const kycSchema = z.object({
	tenantType:       z.enum(['INDIVIDUAL', 'ENTREPRENEUR', 'COMPANY', 'ENTITY']),
	fullName:         z.string().trim().optional(),
	registrationAddr: z.string().trim().optional(),
	phone:            optionalPhone('contacts.editDialog.validation.phoneInvalid'),
	passportSeries:   z.string().trim().optional(),
	passportNumber:   z.string().trim().optional(),
	passportIssuedBy: z.string().trim().optional(),
	inn:              z.string().trim().optional(),
	ogrn:             z.string().trim().optional(),
	kpp:              z.string().trim().optional(),
	attorneyNumber:   z.string().trim().optional(),
	attorneyFrom:     z.string().trim().optional(),
	attorneyTo:       z.string().trim().optional(),
	representative:   z.string().trim().optional(),
	bank:             z.string().trim().optional(),
	bankAccount:      z.string().trim().optional(),
	corrAccount:      z.string().trim().optional(),
	bik:              z.string().trim().optional(),
	signatory:        z.string().trim().optional(),
})

type KycFormValues = z.infer<typeof kycSchema>

interface EditKycDialogProps {
	contactId:    string
	kyc:          ContactKyc | null
	open:         boolean
	onOpenChange: (open: boolean) => void
}

const TENANT_TYPES: TenantType[] = ['INDIVIDUAL', 'ENTREPRENEUR', 'COMPANY', 'ENTITY']

/** Поля, которые и составляют доверенность. */
const PROXY_FIELDS: string[] = ['attorneyNumber', 'attorneyFrom', 'attorneyTo', 'representative']

export function EditKycDialog({ contactId, kyc, open, onOpenChange }: EditKycDialogProps) {
	const { t } = useTranslation()
	const [upsertKyc, { isLoading }] = useUpsertContactKycMutation()
	const [hasError, setHasError] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const hasAnyProxyData = Boolean(kyc?.attorneyNumber || kyc?.attorneyFrom || kyc?.attorneyTo || kyc?.representative)

	const { form, resetDraft } = usePersistedForm<KycFormValues>({
		key:           `contact:kyc:${contactId}`,
		recordVersion: kyc?.updatedAt,
		defaultValues: {
			tenantType:       kyc?.tenantType ?? 'INDIVIDUAL',
			fullName:         kyc?.fullName          ?? '',
			registrationAddr: kyc?.registrationAddr  ?? '',
			phone:            kyc?.phone             ?? '',
			passportSeries:   kyc?.passportSeries    ?? '',
			passportNumber:   kyc?.passportNumber    ?? '',
			passportIssuedBy: kyc?.passportIssuedBy  ?? '',
			inn:              kyc?.inn               ?? '',
			ogrn:             kyc?.ogrn              ?? '',
			kpp:              kyc?.kpp               ?? '',
			attorneyNumber:   kyc?.attorneyNumber    ?? '',
			attorneyFrom:     kyc?.attorneyFrom?.slice(0, 10) ?? '',
			attorneyTo:       kyc?.attorneyTo?.slice(0, 10)   ?? '',
			representative:   kyc?.representative    ?? '',
			bank:             kyc?.bank              ?? '',
			bankAccount:      kyc?.bankAccount       ?? '',
			corrAccount:      kyc?.corrAccount       ?? '',
			bik:              kyc?.bik               ?? '',
			signatory:        kyc?.signatory         ?? '',
		},
		resolver: zodResolverI18n(kycSchema) as Resolver<KycFormValues>,
		mode:     'onBlur',
		active:   open,
	})

	// The parent keeps this dialog mounted, so error state would otherwise
	// survive close/reopen — clear it whenever the dialog opens.
	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasError(false)
			setErrorMsg(undefined)
		}
	}, [open])

	const tenantType = form.watch('tenantType')
	/**
	 * «По доверенности» — состояние экрана, а не поле анкеты.
	 *
	 * В базе такой колонки нет: доверенность — это заполненные номер, срок и
	 * представитель. Пока признак жил в схеме, его приходилось выкидывать перед
	 * отправкой, и он лишь описывал видимость блока.
	 */
	const [hasProxy, setHasProxy] = useState(hasAnyProxyData)

	async function onSubmit(values: KycFormValues) {
		const cleaned = Object.fromEntries(
			Object.entries(values).map(([k, v]) => {
				// Снятая галочка стирает доверенность целиком: показывать поля и
				// сохранять их содержимое — разные вещи.
				if (!hasProxy && PROXY_FIELDS.includes(k)) return [k, undefined]
				return [k, typeof v === 'string' && v.trim() === '' ? undefined : v]
			}),
		) as KycFormValues
		setHasError(false)
		setErrorMsg(undefined)
		try {
			await upsertKyc({ contactId, body: cleaned }).unwrap()
			resetDraft()
			onOpenChange(false)
		} catch (err) {
			setHasError(true)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	const { handleOpenChange, confirmOpen, handleConfirmDiscard, handleCancelDiscard } = useCloseGuard(
		form, onOpenChange, { isLoading, onReset: resetDraft },
	)

	return (
		<>
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetContent side="right" showCloseButton className="flex flex-col gap-0 p-0 sm:max-w-2xl overflow-hidden">
				<SheetHeader className="border-b border-(--color-border) px-6 py-5">
					<SheetTitle>{t('tenants.kyc.dialog.title')}</SheetTitle>
				</SheetHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
						<div className="flex flex-col gap-4 overflow-y-auto flex-1 px-6 py-4">
							<SelectField
								name="tenantType"
								label={t('tenants.kyc.tenantType')}
								options={TENANT_TYPES.map((tt) => ({ value: tt, label: t(`tenants.kyc.types.${tt}`) }))}
							/>

							{/* Common: identification */}
							<section className="flex flex-col gap-3">
								<SectionHeading>{t('tenants.kyc.sections.identification')}</SectionHeading>
								<TextField name="fullName"         label={t('tenants.kyc.fields.fullName')} />
								<TextField name="registrationAddr" label={t('tenants.kyc.fields.registrationAddr')} />
								{/* Explicit primitives rather than TextField: the phone needs
								    the masked input, and TextField renders a plain one. */}
								<FormField
									control={form.control}
									name="phone"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('tenants.kyc.fields.phone')}</FormLabel>
											<FormControl>
												{isMaskSafePhone(kyc?.phone) ? (
													<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
												) : (
													<Input {...field} value={field.value ?? ''} />
												)}
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</section>

							{/* INDIVIDUAL only: passport */}
							{tenantType === 'INDIVIDUAL' && (
								<section className="flex flex-col gap-3">
									<SectionHeading>{t('tenants.kyc.sections.passport')}</SectionHeading>
									<div className="grid grid-cols-2 gap-3">
										<TextField name="passportSeries"   label={t('tenants.kyc.fields.passportSeries')} />
										<TextField name="passportNumber"   label={t('tenants.kyc.fields.passportNumber')} />
									</div>
									<TextField name="passportIssuedBy" label={t('tenants.kyc.fields.passportIssuedBy')} />
								</section>
							)}

							{/* Legal IDs: INN for all; OGRN for ENTREPRENEUR/COMPANY; KPP for COMPANY only */}
							<section className="flex flex-col gap-3">
								<SectionHeading>{t('tenants.kyc.sections.legalIds')}</SectionHeading>
								{tenantType === 'INDIVIDUAL' && (
									<TextField name="inn" label={t('tenants.kyc.fields.inn')} />
								)}
								{tenantType === 'ENTREPRENEUR' && (
									<div className="grid grid-cols-2 gap-3">
										<TextField name="inn"  label={t('tenants.kyc.fields.inn')} />
										<TextField name="ogrn" label={t('tenants.kyc.fields.ogrn')} />
									</div>
								)}
								{(tenantType === 'COMPANY' || tenantType === 'ENTITY') && (
									<div className="grid grid-cols-3 gap-3">
										<TextField name="inn"  label={t('tenants.kyc.fields.inn')} />
										<TextField name="ogrn" label={t('tenants.kyc.fields.ogrn')} />
										<TextField name="kpp"  label={t('tenants.kyc.fields.kpp')} />
									</div>
								)}
							</section>

							{/* Bank details: ENTREPRENEUR and COMPANY only */}
							{tenantType !== 'INDIVIDUAL' && (
								<section className="flex flex-col gap-3">
									<SectionHeading>{t('tenants.kyc.sections.bankDetails')}</SectionHeading>
									<div className="grid grid-cols-2 gap-3">
										<TextField name="bank"        label={t('tenants.kyc.fields.bank')} />
										<TextField name="bik"         label={t('tenants.kyc.fields.bik')} />
										<TextField name="bankAccount" label={t('tenants.kyc.fields.bankAccount')} />
										<TextField name="corrAccount" label={t('tenants.kyc.fields.corrAccount')} />
									</div>
								</section>
							)}

							{/* Common: signatory + proxy */}
							<section className="flex flex-col gap-3">
								<SectionHeading>{t('tenants.kyc.sections.signatory')}</SectionHeading>
								<TextField name="signatory" label={t('tenants.kyc.fields.signatory')} />

								<label className="flex items-center gap-2 py-1 cursor-pointer">
									<Checkbox checked={hasProxy} onCheckedChange={(c) => setHasProxy(c === true)} />
									<span className="text-sm font-[500]">{t('tenants.kyc.sections.byProxy')}</span>
								</label>

								{hasProxy && (
									<>
										<TextField name="representative" label={t('tenants.kyc.fields.representative')} />
										<div className="grid grid-cols-3 gap-3">
											<TextField name="attorneyNumber" label={t('tenants.kyc.fields.attorneyNumber')} />
											<DateField name="attorneyFrom"   label={t('tenants.kyc.fields.attorneyFrom')} />
											<DateField name="attorneyTo"     label={t('tenants.kyc.fields.attorneyTo')} />
										</div>
									</>
								)}
							</section>

							{hasError && (
								<Alert variant="destructive">
									<AlertCircleIcon className="size-4" />
									<AlertTitle>{t('tenants.kyc.dialog.errors.save')}</AlertTitle>
									{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
								</Alert>
							)}
						</div>

						<SheetFooter className="flex-row justify-end gap-2 border-t border-(--color-border) px-6 py-4">
							<Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
								{t('tenants.kyc.dialog.cancel')}
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? t('common.saving') : t('tenants.kyc.dialog.save')}
							</Button>
						</SheetFooter>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
		<ConfirmDialog
			open={confirmOpen}
			onOpenChange={(v) => { if (!v) handleCancelDiscard() }}
			title={t('common.unsavedChanges.title')}
			description={t('common.unsavedChanges.description')}
			confirmLabel={t('common.unsavedChanges.confirm')}
			cancelLabel={t('common.unsavedChanges.cancel')}
			variant="destructive"
			onConfirm={handleConfirmDiscard}
		/>
		</>
	)
}
